import type { DetailState } from '../hooks/useItemDetail';
import { fmtDateTime, fmtPct, fmtPrice } from '../utils/format';

/**
 * Price history for an expanded row.
 *
 * Shows the sync timestamp rather than just the date — the review noted that a
 * bare day is ambiguous when several checks land for the same item in one run.
 */
export default function CheckDetailPanel({
  state,
  currentId,
  limit = 5,
}: {
  state: DetailState;
  /** The check the row is showing, marked so it stands out in the list. */
  currentId?: number;
  limit?: number;
}) {
  if (state.loading) {
    return <p className="text-[12.5px] text-muted-foreground">Loading history…</p>;
  }
  if (state.error) {
    return <p className="text-[12.5px] text-red-600">{state.error}</p>;
  }

  const history = state.data?.history ?? [];
  if (history.length === 0) {
    return <p className="text-[12.5px] text-muted-foreground">No previous checks for this item.</p>;
  }

  return (
    <div className="flex flex-col gap-1.5 text-[12.5px]">
      {history.slice(0, limit).map(entry => (
        <div
          key={entry.id}
          className={[
            'flex items-center gap-2.5 rounded-md border px-2.5 py-1.5',
            entry.id === currentId ? 'border-zinc-300 bg-white' : 'border-border bg-white',
          ].join(' ')}
        >
          <span className="w-[104px] shrink-0 text-muted-foreground">
            {fmtDateTime(entry.price_date ?? entry.created_at)}
          </span>
          <span className="font-medium">{fmtPrice(entry.vendor_price)}</span>
          <span
            className={[
              'ml-auto',
              entry.pct_diff > 0 ? 'text-amber-700' : entry.pct_diff < 0 ? 'text-green-700' : 'text-muted-foreground',
            ].join(' ')}
          >
            {fmtPct(entry.pct_diff)}
          </span>
        </div>
      ))}
      {history.length > limit && (
        <span className="text-[11.5px] text-muted-foreground">
          +{history.length - limit} older {history.length - limit === 1 ? 'check' : 'checks'}
        </span>
      )}
    </div>
  );
}
